import { useState, useRef, useEffect, type ReactNode } from "react";
import type { Asset } from "@/data/types";
import { ChevronIcon } from "@/assets/icons/ChevronIcon";

export function Fld({
  label,
  value,
  tone,
}: {
  label: string;
  value: ReactNode;
  tone?: "pos" | "warn";
}) {
  return (
    <div className="fld">
      <span className="m-label">{label}</span>
      <div className={["val", tone].filter(Boolean).join(" ")}>{value}</div>
    </div>
  );
}

export function Card({
  title,
  note,
  children,
}: {
  title: string;
  note?: string;
  children: ReactNode;
}) {
  return (
    <div className="pcard">
      <header>
        <h3>{title}</h3>
        {note && <span className="note">{note}</span>}
      </header>
      {children}
    </div>
  );
}

export function Totals({ items }: { items: Array<[string, string]> }) {
  return (
    <div className="ptot">
      {items.map(([label, value]) => (
        <div className="t" key={label}>
          <span className="m-label">{label}</span>
          <span className="tv">{value}</span>
        </div>
      ))}
    </div>
  );
}

export function PBar({ value }: { value: number }) {
  return (
    <span className="pbar">
      <span className="bar">
        <i
          className={value >= 100 ? undefined : "mid"}
          style={{ width: `${value}%` }}
        />
      </span>
      <span className="v">{value}</span>
    </span>
  );
}

export function PaneHeader({
  asset,
  h2,
  sub,
}: {
  asset: Asset;
  h2: ReactNode;
  sub: ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(asset.versions.at(-1)!.id);
  const ref = useRef<HTMLDivElement>(null);
  const latestId = asset.versions.at(-1)!.id;

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node))
        setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const selectedVer = asset.versions.find((v) => v.id === selected)!;

  return (
    <div className="pane-header">
      <div className="pane-header-left">
        <h2>{h2}</h2>
        <div className="sub">{sub}</div>
      </div>
      <div className="ver-dd" ref={ref}>
        <button className="ver-trigger" onClick={() => setOpen((o) => !o)}>
          <span className="ver-id">{selectedVer.id}</span>
          <span className="ver-meta">
            · {selectedVer.date}
            {selected === latestId && (
              <span className="ver-latest"> (LATEST)</span>
            )}
          </span>
          <ChevronIcon />
        </button>
        {open && (
          <ul className="ver-list">
            {[...asset.versions].reverse().map((v) => (
              <li
                key={v.id}
                className={`ver-opt${v.id === selected ? " cur" : ""}`}
                onClick={() => {
                  setSelected(v.id);
                  setOpen(false);
                }}
              >
                <span className="ver-id">{v.id}</span>
                <span className="ver-meta">
                  · {v.date}
                  {v.id === latestId && (
                    <span className="ver-latest"> (LATEST)</span>
                  )}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
